// 备份管理页(admin) — Fluent UI v9 单列列表风格
// 点击行展开详情 Dialog(显示完整 SHA256 + 下载/删除操作)
// 消息反馈:顶部 Toast
// 分页固定在底部(与 浏览/我的仓库 一致):root height:100% + body(flex:1,滚动) + pagination(flexShrink:0)
import { useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Badge,
  Title3,
  Text,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
} from '@fluentui/react-components';
import {
  CloudRegular,
  ArrowDownloadRegular,
  DeleteRegular,
  ArrowSyncRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api/admin';
import { explainError } from '@/api/client';
import { SimpleList, type SimpleListItem } from '@/components/SimpleList';
import { DangerButton } from '@/components/DangerButton';
import { toast } from '@/lib/toast';

const PAGE_SIZE = 20;

const BACKUP_KIND_META: Record<string, { color: 'brand' | 'subtle'; label: string }> = {
  manual: { color: 'brand', label: '手动' },
  scheduled: { color: 'subtle', label: '定时' },
};

function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: tokens.spacingVerticalXL,
  },
  spacer: { flex: 1 },
  // 列表区 flex:1 独占剩余高度并滚动,分页才贴底
  body: {
    flex: 1,
    overflowY: 'auto',
    paddingBottom: tokens.spacingVerticalL,
  },
  pagination: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: '10px 0',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  pageInfo: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  fileIcon: {
    fontSize: '22px',
    color: tokens.colorBrandForeground1,
  },
  fileName: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
  },
  // ── 详情描述网格 1 列 ──
  descGrid: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  descRow: {
    display: 'flex',
    alignItems: 'stretch',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':last-child': { borderBottom: 'none' },
  },
  descLabel: {
    width: '110px',
    flexShrink: 0,
    padding: '10px 16px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    alignItems: 'center',
  },
  descValue: {
    flex: 1,
    padding: '10px 16px',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    wordBreak: 'break-all',
  },
  sha: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    wordBreak: 'break-all',
  },
});

export function AdminBackupPage() {
  const styles = useStyles();
  const qc = useQueryClient();
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['backups'],
    queryFn: adminApi.listBackups,
  });

  const triggerMut = useMutation({
    mutationFn: adminApi.triggerBackup,
    onSuccess: () => {
      toast.success('备份已触发');
      qc.invalidateQueries({ queryKey: ['backups'] });
    },
    onError: (e) => toast.error(explainError(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (name: string) => adminApi.deleteBackup(name),
    onSuccess: () => {
      toast.success('备份已删除');
      setSelectedName(null);
      setConfirmDelete(false);
      qc.invalidateQueries({ queryKey: ['backups'] });
    },
    onError: (e) => toast.error(explainError(e)),
  });

  const files = data?.files ?? [];
  const selected = files.find((f) => f.name === selectedName) ?? null;

  const total = files.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // 客户端分页:只把当前页传给 SimpleList
  const pagedFiles = useMemo(
    () => files.slice((pageIndex - 1) * PAGE_SIZE, (pageIndex - 1) * PAGE_SIZE + PAGE_SIZE),
    [files, pageIndex],
  );

  const items: SimpleListItem[] = useMemo(
    () =>
      pagedFiles.map((f) => {
        const kindMeta = BACKUP_KIND_META[f.kind] ?? BACKUP_KIND_META.manual;
        return {
          key: f.name,
          media: <CloudRegular className={styles.fileIcon} />,
          title: (
            <>
              <Badge appearance="filled" color={kindMeta.color}>
                {kindMeta.label}
              </Badge>
              <span className={styles.fileName}>{f.name}</span>
            </>
          ),
          description: `${formatBytes(f.size)} · ${new Date(f.created_at).toLocaleString('zh-CN')}`,
          extra: <Badge appearance="tint" color="informative">{formatBytes(f.size)}</Badge>,
          onClick: () => setSelectedName(f.name),
        };
      }),
    [pagedFiles, styles.fileIcon, styles.fileName],
  );

  async function onDownload() {
    if (!selected) return;
    setDownloading(true);
    try {
      await adminApi.downloadBackup(selected.name);
    } catch (e) {
      toast.error(explainError(e));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className={styles.root}>
      <Title3 as="h1">备份管理</Title3>

      <div className={styles.toolbar}>
        <div className={styles.spacer} />
        <Button
          appearance="primary"
          icon={<ArrowSyncRegular />}
          disabled={triggerMut.isPending}
          onClick={() => triggerMut.mutate()}
        >
          {triggerMut.isPending ? '备份中…' : '立即备份'}
        </Button>
      </div>

      {/* 列表区(单独滚动,分页贴底) */}
      <div className={styles.body}>
        <SimpleList
          items={items}
          loading={isLoading}
          emptyText="暂无备份"
          pageSize={Number.POSITIVE_INFINITY}
        />
      </div>

      {/* 分页固定底部 */}
      {total > 0 && (
        <div className={styles.pagination}>
          <Button
            appearance="subtle"
            icon={<ChevronLeftRegular />}
            disabled={pageIndex <= 1}
            onClick={() => setPageIndex((p) => Math.max(1, p - 1))}
          />
          <Text className={styles.pageInfo}>
            第 {pageIndex} / {totalPages} 页 · 共 {total} 个
          </Text>
          <Button
            appearance="subtle"
            icon={<ChevronRightRegular />}
            disabled={pageIndex >= totalPages}
            onClick={() => setPageIndex((p) => Math.min(totalPages, p + 1))}
          />
        </div>
      )}

      {/* 备份详情 */}
      <Dialog open={!!selected} onOpenChange={(_, d) => !d.open && setSelectedName(null)} modalType="alert">
        <DialogSurface>
          <DialogBody>
            <DialogTitle>备份详情</DialogTitle>
            <DialogContent>
              {selected && (
                <div className={styles.descGrid}>
                  <div className={styles.descRow}>
                    <div className={styles.descLabel}>文件名</div>
                    <div className={styles.descValue}>
                      <code>{selected.name}</code>
                    </div>
                  </div>
                  <div className={styles.descRow}>
                    <div className={styles.descLabel}>类型</div>
                    <div className={styles.descValue}>
                      <Badge
                        appearance="filled"
                        color={
                          (BACKUP_KIND_META[selected.kind] ?? BACKUP_KIND_META.manual).color
                        }
                      >
                        {(BACKUP_KIND_META[selected.kind] ?? BACKUP_KIND_META.manual).label}
                      </Badge>
                    </div>
                  </div>
                  <div className={styles.descRow}>
                    <div className={styles.descLabel}>大小</div>
                    <div className={styles.descValue}>{formatBytes(selected.size)}</div>
                  </div>
                  <div className={styles.descRow}>
                    <div className={styles.descLabel}>SHA256</div>
                    <div className={styles.descValue}>
                      <code className={styles.sha}>{selected.sha256}</code>
                    </div>
                  </div>
                  <div className={styles.descRow}>
                    <div className={styles.descLabel}>创建时间</div>
                    <div className={styles.descValue}>
                      {new Date(selected.created_at).toLocaleString('zh-CN')}
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">关闭</Button>
              </DialogTrigger>
              <Button
                appearance="outline"
                icon={<ArrowDownloadRegular />}
                disabled={downloading}
                onClick={onDownload}
              >
                {downloading ? '下载中…' : '下载'}
              </Button>
              <DangerButton
                icon={<DeleteRegular />}
                loading={deleteMut.isPending}
                onClick={() => setConfirmDelete(true)}
              >
                删除
              </DangerButton>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 删除确认(嵌套) */}
      <Dialog open={confirmDelete} onOpenChange={(_, d) => setConfirmDelete(d.open)} modalType="alert">
        <DialogSurface>
          <DialogBody>
            <DialogTitle>确认删除此备份?</DialogTitle>
            <DialogContent>删除后无法恢复</DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">取消</Button>
              </DialogTrigger>
              <DangerButton
                loading={deleteMut.isPending}
                onClick={() => selected && deleteMut.mutate(selected.name)}
              >
                删除
              </DangerButton>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
